import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button
} from '@mui/material';
import {
  Refresh
} from '@mui/icons-material';

const CaptchaSection = ({ 
  captchaAnswer, 
  onCaptchaChange, 
  captchaError, 
  loading = false,
  onCaptchaGenerate 
}) => {
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, operator: '+', answer: 0 });

  // Generate new CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-', '×'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer;
    switch (operator) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        answer = num1 - num2;
        break;
      case '×':
        answer = num1 * num2;
        break;
      default:
        answer = num1 + num2;
    }
    
    const newCaptcha = { num1, num2, operator, answer };
    setCaptcha(newCaptcha);
    
    // Call parent callback if provided
    if (onCaptchaGenerate) {
      onCaptchaGenerate(newCaptcha);
    }
  };

  // Generate initial CAPTCHA on component mount
  useEffect(() => {
    generateCaptcha();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCaptchaChange = (event) => {
    onCaptchaChange(event.target.value);
  };

  return (
    <Card sx={{ 
      backgroundColor: '#ffffff', 
      border: captchaError ? '2px solid #d32f2f' : '1px solid #e0e0e0',
      mx: { xs: 0, sm: 1 }
    }}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 },
        '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } }
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            color: '#2259c9', 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
          }}
        >
          Security Verification
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: { xs: 2, sm: 2.5, md: 3 }, 
            color: '#666',
            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
            lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
          }}
        >
          Please solve this simple math problem to verify you're human:
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 1.5, md: 2 }, 
          mb: { xs: 1.5, sm: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#2259c9',
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              minWidth: { xs: '120px', sm: 'auto' }
            }}
          >
            {captcha.num1} {captcha.operator} {captcha.num2} = ?
          </Typography>
          <Button
            onClick={generateCaptcha}
            startIcon={<Refresh sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
            size="small"
            disabled={loading}
            sx={{ 
              color: '#2259c9',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.5, sm: 0.75 },
              minHeight: { xs: '32px', sm: '36px' },
              '&:hover': { backgroundColor: '#f0f4ff' }
            }}
          >
            New
          </Button>
        </Box>
        
        <TextField
          fullWidth
          label="Your Answer"
          type="number"
          value={captchaAnswer}
          onChange={handleCaptchaChange}
          required
          disabled={loading}
          variant="outlined"
          error={captchaError}
          helperText={captchaError ? 'Incorrect answer. Please try again.' : ''}
          sx={{ 
            maxWidth: { xs: '100%', sm: 200 },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.85rem', sm: '0.9rem' }
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.9rem', sm: '1rem' },
              padding: { xs: '10px 12px', sm: '12px 14px' }
            },
            '& .MuiFormHelperText-root': {
              fontSize: { xs: '0.75rem', sm: '0.8rem' }
            }
          }}
        />
      </CardContent>
    </Card>
  );
};

export default CaptchaSection; 